import * as React from 'react';
import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { DetailsList, DetailsListLayoutMode, IDetailsRowProps, DetailsRow } from '@fluentui/react/lib/DetailsList';
import { TooltipHost, TooltipOverflowMode } from '@fluentui/react/lib/Tooltip';
import '../styles/common.css'; // Import CSS styles
import 'react-date-range/dist/styles.css'; // Import the default styles
import 'react-date-range/dist/theme/default.css'; // Import the default theme styles
import 'font-awesome/css/font-awesome.min.css'; // Import Font Awesome icons
import { Metrics, MetricNames } from '../utils/constants';
import AzDevOpsIcon from '../assets/images/Azure-DevOps.svg';
import { FarmNameOptions, getFarmNameOptions } from '../utils/constants';
import { useConfig } from "../configContext";

const _columns = [
    {
        key: 'prTitle', name: 'PR Title', fieldName: 'prTitle', isResizable: true,
        minWidth: 300,
        maxWidth: 550,
        onRender: (item) => {
            const serviceName = localStorage.getItem('selectedService');
            return (<Link to={`/service/${serviceName}/CommitDetails/${item.commitMetaData.commitId}`}>{item.commitMetaData.title}</Link>);
        }
    },
    {
        key: 'authorName', name: 'Author Name', fieldName: 'authorName', isResizable: true,
        minWidth: 100,
        maxWidth: 200,
        onRender: (item) => item.commitMetaData.authorName
    },
    {
        key: 'eventIdentifier', name: 'Event Identifier', fieldName: 'eventIdentifier', isResizable: true,
        minWidth: 300,
        maxWidth: 400,
        onRender: eventIdentifierRender
    },
    {
        key: 'eventDate', name: 'Event Date', fieldName: 'eventDate', isResizable: true,
        minWidth: 150,
        maxWidth: 200,
        onRender: (item) => {
            const eventStartDate = new Date(item.startTime);
            const formattedDate = eventStartDate.toLocaleDateString('en-US', { year: 'numeric', month: '2-digit', day: '2-digit' });
            const formattedTime = eventStartDate.toLocaleTimeString('en-US', {
                hour: '2-digit',
                minute: '2-digit',
                second: '2-digit',
                hour12: true
            });
            return `${formattedDate}, ${formattedTime}`;
        }
    },
    {
        key: 'eventAction', name: 'Action', fieldName: 'eventAction', isResizable: true,
        minWidth: 50,
        maxWidth: 70,
        onRender: (item) => (
            (item.type === 0 || item.type === 1) && item.action === 1
                ? <i className="fa fa-toggle-on" style={{ color: '#0078d4', fontSize: '20px' }} aria-hidden="true"></i>
                : (item.type === 0 || item.type === 1) && item.action === 0
                    ? <i className="fa fa-toggle-off" style={{ color: '#0078d4', fontSize: '20px' }} aria-hidden="true"></i>
                    : null
        )
    },
    {
        key: 'eventType', name: 'Type', fieldName: 'eventType', isResizable: true,
        minWidth: 80,
        maxWidth: 100,
        onRender: (item) => item.type === 0 ? 'KillSwitch' : item.type === 1 ? 'Flight'
            : item.type === 2 || item.type === 3 ? 'Deployment' : item.type === 4 ? 'ULS detection' : 'Unknown'
    },
    {
        key: 'isRegression', name: 'Regression', fieldName: 'isRegression', isResizable: true,
        minWidth: 80,
        maxWidth: 120,
        onRender: (item) => item.commitMetaData.dataSources === null && item.commitMetaData.regressionMask === 0
            ? 'no data'
            : Metrics.filter(v => (item.commitMetaData.regressionMask & Math.pow(2, v)) !== 0)
                .map(m => MetricNames[m])
                .join(', ')
    },
    {
        key: 'AzDevops', name: 'AzDevops', fieldName: 'AzDevops', isResizable: true,
        minWidth: 80,
        maxWidth: 100,
        onRender: (item) => (
            <a
                href={`https://onedrive.visualstudio.com/${item.commitMetaData.projectName}/_git/${item.commitMetaData.repositoryName}/commit/${item.commitMetaData.commitId.split('-').pop()}`}
                target="_blank"
                rel="noopener noreferrer"
            >
                <img src={AzDevOpsIcon} width="20" height="20" alt="Link to commit in AzDevops " />
            </a>
        )
    }
];

function eventIdentifierRender(item) {
    return (
        <TooltipHost content={item.eventIdentifier} overflowMode={TooltipOverflowMode.Self}>
            <span className="overflow-text" title={item.eventIdentifier}>{item.eventIdentifier}</span>
        </TooltipHost>
    );
}

export function EventListView({ commits, auditorView, navState }) {
    const config = useConfig();
    const serviceName = localStorage.getItem('selectedService');
    const service = config.services.find(s => s.name === serviceName);
    const farmNameOptions = getFarmNameOptions(service);
    const farmNameOption = farmNameOptions.find(option => option.value === navState.farmType);
    const farmNameText = farmNameOption ? farmNameOption.text : '';

    const items = Object.keys(commits).reduce((acc, key) => {
        const commit = commits[key];
        const commitEvents = commit.commitEvents
            .filter(event => event.farmName.toLowerCase().includes(farmNameText.toLowerCase()))
            .map((event) => ({
                ...event,
                eventIdentifier: event.identifier,
                commitMetaData: commit.commitMetaData
            }));
        return acc.concat(commitEvents);
    }, []).sort((a, b) => new Date(b.startTime).getTime() - new Date(a.startTime).getTime());

    return (
        <DetailsList
            compact={true}
            items={items}
            columns={_columns}
            onRenderRow={_onRenderRow}
            setKey="set"
            layoutMode={DetailsListLayoutMode.justified}
            selectionMode={0}
            ariaLabelForSelectionColumn="Toggle selection"
            ariaLabelForSelectAllCheckbox="Toggle selection for all items"
            checkButtonAriaLabel="select row"
            styles={{ root: { marginTop: '-3px' } }}
        />
    );

    function _onRenderRow(props: IDetailsRowProps | undefined): JSX.Element {
        if (!props || !props.item) {
            return null;
        }

        const rowClass = props.itemIndex % 2 === 0 ? 'evenRow' : 'oddRow';

        return (
            <DetailsRow
                {...props}
                styles={{
                    root: {
                        backgroundColor: rowClass === 'oddRow' ? 'white' : '#f2f2f2',
                        selectors: {
                            ':hover': {
                                backgroundColor: '#e5f1fa'
                            }
                        }
                    }
                }}
            />
        );
    }
}
